import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Stage, Layer, Rect, Shape, Transformer, Group, Text } from "react-konva";
import { Box, IconButton, MenuItem, Paper, Select, Tooltip, Divider } from "@mui/material";
import { NearMe, PanTool, Add, Remove, FitScreen } from "@mui/icons-material";
import useEditorMapa, { useMapaActual, useTarifas } from "../../stores/useEditorMapa";
import { actualizarSector, actualizarDecorativo, conLugares, cajaMapa, coloresDeTarifas, colorDeUbicacion } from "../../domain/mapa/mapa";
import { ajustarAGrilla, limitar } from "../../domain/mapa/geometria";
import { moverMesa, aplicarFilas, configFilas, ajustarFila, acomodarMesas, tipoDe, TIPO_UBICACION, MAX_FILAS, MAX_ASIENTOS_POR_FILA } from "../../domain/mapa/generador";
import { RADIO_ASIENTO } from "../../domain/mapa/geometria";
import { SectorFigura, DecorativoFigura } from "./figuras";
import { cambiarSector } from "./acciones";
import { COLOR, conAlfa } from "./estilos";
import { toast } from "react-toastify";
import {
  alternar,
  alternarObjeto,
  elementosDe,
  estaElegido,
  lugaresEnArea,
  moverObjetos,
  objetosDe,
  objetosEnArea,
  seleccionDe,
  seleccionDeObjetos,
  sumarElementos,
  sumarObjetos,
} from "../../domain/mapa/seleccion";

const ESCALA_MIN = 0.1;
const ESCALA_MAX = 4;
const PASO_GRILLA_VISIBLE = 20;
// desde este zoom se ven los números de los asientos
const ESCALA_DETALLE = 1.1;
const NIVELES_ZOOM = [0.25, 0.5, 0.75, 1, 1.5, 2];

const esCampoDeTexto = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

// Grilla: puntos sólo en la parte visible del lienzo (que es infinito)
const Grilla = ({ x, y, ancho, alto }) => (
  <Shape
    listening={false}
    perfectDrawEnabled={false}
    sceneFunc={(ctx) => {
      ctx.fillStyle = COLOR.puntoGrilla;
      const x0 = Math.floor(x / PASO_GRILLA_VISIBLE) * PASO_GRILLA_VISIBLE;
      const y0 = Math.floor(y / PASO_GRILLA_VISIBLE) * PASO_GRILLA_VISIBLE;
      for (let px = x0; px < x + ancho; px += PASO_GRILLA_VISIBLE) {
        for (let py = y0; py < y + alto; py += PASO_GRILLA_VISIBLE) {
          ctx.fillRect(px - 0.75, py - 0.75, 1.5, 1.5);
        }
      }
    }}
  />
);

const TAM_MANIJA = 16;

// Manija: un "+" que se queda en el borde de lo que agranda (dragBoundFunc no
// la deja seguir al puntero, así no titila) y avanza cuando el borde cambia.
// alMover recibe cuánto se movió el puntero desde el comienzo, en
// coordenadas de la ubicación; se mide en coordenadas del plano para que no
// salte si la ubicación se corre.
const Manija = ({ x, y, escala, cursor, grupoRef, alEmpezar, alMover }) => {
  const tam = TAM_MANIJA / escala;
  const inicio = useRef(null);
  const aLocal = (punto) => grupoRef.current.getAbsoluteTransform().copy().invert().point(punto);
  return (
    <Group
      name="manija"
      x={x}
      y={y}
      draggable
      dragBoundFunc={function () {
        return this.getAbsolutePosition();
      }}
      onDragStart={(e) => {
        inicio.current = e.target.getStage().getPointerPosition();
        alEmpezar?.();
      }}
      onDragMove={(e) => {
        const desde = aLocal(inicio.current);
        const hasta = aLocal(e.target.getStage().getPointerPosition());
        alMover({ x: hasta.x - desde.x, y: hasta.y - desde.y }, hasta);
      }}
      onMouseEnter={(e) => (e.target.getStage().container().style.cursor = cursor)}
      onMouseLeave={(e) => (e.target.getStage().container().style.cursor = "default")}
    >
      <Rect x={-tam} y={-tam} width={tam * 2} height={tam * 2} fill="rgba(0,0,0,0.001)" />
      <Rect x={-tam / 2} y={-tam / 2} width={tam} height={tam} cornerRadius={3 / escala} fill={COLOR.acento} listening={false} />
      <Text x={-tam / 2} y={-tam / 2} width={tam} height={tam} text="+" align="center" verticalAlign="middle" fontSize={13 / escala} fontStyle="bold" fill="#fff" listening={false} />
    </Group>
  );
};

// ManijasFilas: en una ubicación con filas, el "+" del costado suma o saca
// asientos por fila y el de abajo, filas (como en Wix)
const ManijasFilas = ({ sector, escala, onCambiar }) => {
  const grupoRef = useRef(null);
  const base = useRef(null);
  const config = configFilas(sector);
  const empezar = () => (base.current = config);
  return (
    <Group ref={grupoRef} x={sector.x} y={sector.y} rotation={sector.rotacion || 0}>
      <Manija x={sector.ancho} y={sector.alto / 2} escala={escala} cursor="ew-resize" grupoRef={grupoRef} alEmpezar={empezar} alMover={(d) => {
        const c = base.current;
        const n = limitar(c.asientosPorFila + Math.round(d.x / c.separacion), 1, MAX_ASIENTOS_POR_FILA);
        if (n !== config.asientosPorFila) onCambiar({ asientosPorFila: n });
      }} />
      <Manija x={sector.ancho / 2} y={sector.alto} escala={escala} cursor="ns-resize" grupoRef={grupoRef} alEmpezar={empezar} alMover={(d) => {
        const c = base.current;
        const n = limitar(c.filas + Math.round(d.y / c.separacionFilas), 1, MAX_FILAS);
        if (n !== config.filas) onCambiar({ filas: n });
      }} />
    </Group>
  );
};

// ManijasMesas: el "+" del costado cambia el ancho y las mesas se reacomodan
// en renglones (no se crean ni se borran)
const ManijasMesas = ({ sector, escala, onCambiar }) => {
  const grupoRef = useRef(null);
  const base = useRef(null);
  const posiciones = (s) => s.mesas.map((m) => `${m.x},${m.y}`).join("|");
  return (
    <Group ref={grupoRef} x={sector.x} y={sector.y} rotation={sector.rotacion || 0}>
      <Manija
        x={sector.ancho}
        y={sector.alto / 2}
        escala={escala}
        cursor="ew-resize"
        grupoRef={grupoRef}
        alEmpezar={() => (base.current = sector.ancho)}
        alMover={(d) => {
          const ancho = Math.max(base.current + d.x, 80);
          const r = acomodarMesas(sector, ancho);
          if (posiciones(r.sector) !== posiciones(sector)) onCambiar(ancho);
        }}
      />
    </Group>
  );
};

// ManijasFila: los "+" en las puntas de la fila elegida suman o sacan asientos
// sólo a esa fila. En una fila recta, la punta izquierda crece hacia la
// izquierda; en una curva, la fila crece pareja desde el centro.
const ManijasFila = ({ sector, indice, escala, onAjustar }) => {
  const grupoRef = useRef(null);
  const base = useRef(0);
  const fila = sector.filas[indice];
  const config = configFilas(sector);
  if (!fila || fila.asientos.length === 0) return null;
  const n = fila.asientos.length;
  const separacion = config.separacion;
  const distancia = RADIO_ASIENTO + 14;
  const punta = (extremo, vecino) => {
    const largo = vecino ? Math.hypot(extremo.x - vecino.x, extremo.y - vecino.y) || 1 : 1;
    const u = vecino ? { x: (extremo.x - vecino.x) / largo, y: (extremo.y - vecino.y) / largo } : { x: 1, y: 0 };
    return { x: extremo.x + u.x * distancia, y: extremo.y + u.y * distancia, u, extremo };
  };
  const izq = punta(fila.asientos[0], fila.asientos[1]);
  const der = punta(fila.asientos[n - 1], fila.asientos[n - 2]);
  if (n === 1) izq.u = { x: -1, y: 0 };
  const curva = config.curva > 0;

  const alMover = (lado) => (d) => {
    const avance = d.x * lado.u.x + d.y * lado.u.y;
    const nuevo = limitar(base.current + Math.round(avance / separacion), 1, MAX_ASIENTOS_POR_FILA);
    if (nuevo === n) return;
    const corrimiento = lado === izq && !curva ? -(nuevo - n) * separacion : 0;
    onAjustar({ asientos: nuevo, dx: corrimiento });
  };
  const empezar = () => (base.current = n);

  return (
    <Group ref={grupoRef} x={sector.x} y={sector.y} rotation={sector.rotacion || 0}>
      <Manija x={izq.x} y={izq.y} escala={escala} cursor="ew-resize" grupoRef={grupoRef} alEmpezar={empezar} alMover={alMover(izq)} />
      <Manija x={der.x} y={der.y} escala={escala} cursor="ew-resize" grupoRef={grupoRef} alEmpezar={empezar} alMover={alMover(der)} />
    </Group>
  );
};

// lo elegido de un tipo (filas o mesas), en cualquier ubicación
const elegidosDe = (seleccion, tipo) => elementosDe(seleccion).filter((e) => e.tipo === tipo);

const LienzoMapa = forwardRef((_, ref) => {
  const mapa = useMapaActual();
  const seleccion = useEditorMapa((s) => s.seleccion);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const cambiar = useEditorMapa((s) => s.cambiar);
  // en la pestaña de tarifas el plano sólo sirve para elegir sectores
  const soloElegir = useEditorMapa((s) => s.pestana === "tarifas");
  const tarifas = useTarifas();
  // cada ubicación toma el color de su tarifa; sin tarifa, gris
  const colores = useMemo(() => coloresDeTarifas(tarifas), [tarifas]);

  // elegir: un clic elige una fila o mesa; en "Asignar tarifas", con Shift se
  // suman o sacan (también de otras ubicaciones y mezclando filas y mesas)
  const elegir = (elemento, sumar) => {
    if (sumar && soloElegir && elementosDe(seleccion).length > 0) {
      const nueva = alternar(seleccion, elemento);
      if (nueva) seleccionar(nueva);
      return;
    }
    seleccionar(seleccionDe([elemento]));
  };

  const contenedorRef = useRef(null);
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const ajustado = useRef(false);

  const [tamano, setTamano] = useState({ ancho: 0, alto: 0 });
  const [vista, setVista] = useState({ escala: 1, x: 0, y: 0 });
  const [herramienta, setHerramienta] = useState("seleccionar");
  const [espacio, setEspacio] = useState(false);
  // mientras se mueve o rota algo, las manijas se esconden (como en Wix)
  const [ocupado, setOcupado] = useState(false);
  const moviendoPlano = herramienta === "mano" || espacio;

  // area: el rectángulo que se arrastra para elegir varios lugares
  // (la ref sigue al mouse aunque el lienzo todavía no se haya redibujado)
  const [area, setAreaVisible] = useState(null);
  const areaRef = useRef(null);
  const setArea = (cambio) => {
    areaRef.current = typeof cambio === "function" ? cambio(areaRef.current) : cambio;
    setAreaVisible(areaRef.current);
  };
  const terminarArea = () => {
    const area = areaRef.current;
    if (!area) return;
    setArea(null);
    // un clic sin arrastrar en el fondo deja de elegir (con Shift, no cambia nada)
    const chico = Math.abs(area.x2 - area.x1) * vista.escala < 4 && Math.abs(area.y2 - area.y1) * vista.escala < 4;
    if (chico) {
      if (area.sector) seleccionar({ tipo: "sector", clave: area.sector });
      else if (!area.sumar) seleccionar(null);
      return;
    }
    if (!soloElegir) {
      const objetos = objetosEnArea(mapa, area);
      const todos = area.sumar ? sumarObjetos(objetosDe(seleccion), objetos) : objetos;
      if (todos.length > 0 || !area.sumar) seleccionar(seleccionDeObjetos(todos));
      return;
    }
    const { elementos, conVentas } = lugaresEnArea(mapa, area);
    const todos = area.sumar ? sumarElementos(elementosDe(seleccion), elementos) : elementos;
    if (todos.length > 0) seleccionar(seleccionDe(todos));
    else if (!area.sumar) seleccionar(null);
    if (conVentas > 0) {
      toast.info(
        conVentas === 1 ? "Un lugar con ventas no se eligió: no puede cambiar de tarifa." : `${conVentas} lugares con ventas no se eligieron: no pueden cambiar de tarifa.`,
        { toastId: "area-ventas" }
      );
    }
  };

  useEffect(() => {
    const el = contenedorRef.current;
    if (!el) return undefined;
    const observador = new ResizeObserver(([entrada]) => {
      setTamano({ ancho: entrada.contentRect.width, alto: entrada.contentRect.height });
    });
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  const ajustar = useCallback(() => {
    if (!tamano.ancho || !mapa) return;
    const caja = cajaMapa(mapa);
    const escala = limitar(Math.min((tamano.ancho - 120) / caja.ancho, (tamano.alto - 140) / caja.alto), ESCALA_MIN, 1.5);
    setVista({
      escala,
      x: (tamano.ancho - caja.ancho * escala) / 2 - caja.x * escala,
      y: (tamano.alto - caja.alto * escala) / 2 + 20 - caja.y * escala,
    });
  }, [tamano, mapa]);

  useEffect(() => {
    if (!ajustado.current && tamano.ancho && mapa) {
      ajustado.current = true;
      ajustar();
    }
  }, [tamano, mapa, ajustar]);

  // al publicar, el plano vuelve con el origen recalculado: se encuadra de nuevo
  const publicado = useEditorMapa((s) => s.publicado);
  const publicadoAntes = useRef(publicado);
  useEffect(() => {
    if (publicado && publicado !== publicadoAntes.current) ajustar();
    publicadoAntes.current = publicado;
    // sólo cuando cambia la publicación
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicado]);

  const zoomEn = useCallback(
    (nuevaEscala, punto) => {
      const escala = limitar(nuevaEscala, ESCALA_MIN, ESCALA_MAX);
      const centro = punto || { x: tamano.ancho / 2, y: tamano.alto / 2 };
      setVista((v) => {
        const enPlano = { x: (centro.x - v.x) / v.escala, y: (centro.y - v.y) / v.escala };
        return { escala, x: centro.x - enPlano.x * escala, y: centro.y - enPlano.y * escala };
      });
    },
    [tamano]
  );

  useImperativeHandle(
    ref,
    () => ({
      // punto del plano que está en el medio de la pantalla: ahí se agrega lo nuevo
      centroVisible: () => ({
        x: ajustarAGrilla((tamano.ancho / 2 - vista.x) / vista.escala),
        y: ajustarAGrilla((tamano.alto / 2 - vista.y) / vista.escala),
      }),
    }),
    [tamano, vista]
  );

  // barra espaciadora: mover el plano mientras se mantiene apretada
  useEffect(() => {
    const abajo = (e) => {
      if (e.code === "Space" && !esCampoDeTexto(document.activeElement)) {
        e.preventDefault();
        setEspacio(true);
      }
    };
    const arriba = (e) => e.code === "Space" && setEspacio(false);
    window.addEventListener("keydown", abajo);
    window.addEventListener("keyup", arriba);
    return () => {
      window.removeEventListener("keydown", abajo);
      window.removeEventListener("keyup", arriba);
    };
  }, []);

  const alGirarRueda = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (e.evt.ctrlKey || e.evt.metaKey) {
      const factor = Math.exp(-e.evt.deltaY * 0.0025);
      zoomEn(vista.escala * factor, stage.getPointerPosition());
    } else {
      setVista((v) => ({ ...v, x: v.x - e.evt.deltaX, y: v.y - e.evt.deltaY }));
    }
  };

  const seleccionado = useMemo(() => {
    if (!seleccion || !mapa) return null;
    if (seleccion.tipo === "sector") return mapa.sectores.find((s) => s.clave === seleccion.clave) || null;
    return mapa.decorativos.find((d) => d.clave === seleccion.clave) || null;
  }, [seleccion, mapa]);

  useEffect(() => {
    const transformer = transformerRef.current;
    const stage = stageRef.current;
    if (!transformer || !stage) return;
    // con varios elegidos el marco los abarca a todos y se mueven juntos
    const claves = soloElegir ? [] : seleccion?.tipo === "varios" ? seleccion.elementos.map((e) => e.clave) : seleccionado ? [seleccionado.clave] : [];
    transformer.nodes(claves.map((c) => stage.findOne(`#${c}`)).filter(Boolean));
    transformer.getLayer()?.batchDraw();
  }, [seleccionado, seleccion, mapa, soloElegir]);

  // con varios elegidos, soltar uno corre a todos lo mismo
  const varios = seleccion?.tipo === "varios" ? seleccion.elementos : null;
  // (cada nodo del grupo avisa que terminó de moverse: se aplica una sola vez)
  const yaMovido = useRef(false);
  const moverVarios = (tipo, clave, dx, dy) => {
    if (!varios || !estaElegido(seleccion, tipo, clave)) return false;
    if (yaMovido.current) return true;
    yaMovido.current = true;
    setTimeout(() => (yaMovido.current = false), 0);
    if (dx || dy) cambiar((m) => moverObjetos(m, varios, dx, dy));
    return true;
  };
  // clic: elige; con Shift suma o saca; sobre algo ya elegido entre varios, los deja (para arrastrarlos)
  const elegirObjeto = (tipo, clave, e) => {
    if (moviendoPlano) return;
    if (e?.evt?.shiftKey) {
      seleccionar(alternarObjeto(seleccion, { tipo, clave }));
      return;
    }
    if (varios && estaElegido(seleccion, tipo, clave)) return;
    seleccionar({ tipo, clave });
  };

  const ajustarArrastre = (e) => {
    const nodo = e.target;
    nodo.x(ajustarAGrilla(nodo.x()));
    nodo.y(ajustarAGrilla(nodo.y()));
  };

  // el lienzo es infinito: el tamaño del plano se calcula al guardar
  const arrastrarSector = (sector) => (e) => {
    if (e.target.id() !== sector.clave) return;
    e.target.x(ajustarAGrilla(e.target.x()));
    e.target.y(ajustarAGrilla(e.target.y()));
  };

  const moverSectorFin = (sector) => (e) => {
    // también llega el fin de arrastrar una fila o una mesa de adentro
    if (e.target.id() !== sector.clave) return;
    const { x, y } = e.target.position();
    if (moverVarios("sector", sector.clave, Math.round(x) - sector.x, Math.round(y) - sector.y)) return;
    cambiar((m) => actualizarSector(m, sector.clave, { x: Math.round(x), y: Math.round(y) }));
  };

  const moverDecorativoFin = (d) => (e) => {
    const { x, y } = e.target.position();
    const dx = Math.round(x) - d.x;
    const dy = Math.round(y) - d.y;
    if (moverVarios("decorativo", d.clave, dx, dy)) return;
    const cambios = { x: d.x + dx, y: d.y + dy };
    if (d.tipo === "linea") {
      cambios.punto_final_x = (d.punto_final_x ?? d.x) + dx;
      cambios.punto_final_y = (d.punto_final_y ?? d.y) + dy;
    }
    cambiar((m) => actualizarDecorativo(m, d.clave, cambios));
  };

  const transformarFin = (e) => {
    if (!seleccionado) return;
    const nodo = e.target;
    const sx = nodo.scaleX();
    const sy = nodo.scaleY();
    nodo.scaleX(1);
    nodo.scaleY(1);
    const base = { x: ajustarAGrilla(nodo.x()), y: ajustarAGrilla(nodo.y()), rotacion: Math.round(nodo.rotation()) };

    if (seleccion.tipo === "sector") {
      const s = seleccionado;
      const cambios = conLugares(s)
        ? base
        : {
            ...base,
            ancho: Math.max(40, ajustarAGrilla(s.ancho * sx)),
            alto: Math.max(30, ajustarAGrilla(s.alto * sy)),
            forma: s.forma.map((p) => ({ x: Math.round(p.x * sx), y: Math.round(p.y * sy) })),
          };
      cambiar((m) => actualizarSector(m, s.clave, cambios));
      return;
    }

    const d = seleccionado;
    const cambios = { ...base };
    if (d.tipo === "texto") cambios.font_size = Math.max(8, Math.round((d.font_size || 18) * sy));
    if (d.tipo === "rectangulo" || d.tipo === "circulo") {
      cambios.ancho = Math.max(10, Math.round(d.ancho * sx));
      cambios.alto = Math.max(10, Math.round(d.alto * sy));
    }
    if (d.tipo === "linea") {
      cambios.punto_final_x = base.x + Math.round(((d.punto_final_x ?? d.x) - d.x) * sx);
      cambios.punto_final_y = base.y + Math.round(((d.punto_final_y ?? d.y) - d.y) * sy);
    }
    cambiar((m) => actualizarDecorativo(m, d.clave, cambios));
  };

  if (!mapa) return null;

  const sectoresVisibles = mapa.sectores.filter((s) => s.en_mapa);
  const decorativosAbajo = mapa.decorativos.filter((d) => (d.z_index || 0) < 1);
  const decorativosArriba = mapa.decorativos.filter((d) => (d.z_index || 0) >= 1);
  const redimensionable =
    seleccionado && !(seleccion?.tipo === "sector" && conLugares(seleccionado)) && seleccionado.tipo !== "texto";

  const decorativo = (d) => (
    <DecorativoFigura
      key={d.clave}
      decorativo={d}
      seleccionable={!soloElegir}
      arrastrable={!moviendoPlano}
      onSeleccionar={(e) => elegirObjeto("decorativo", d.clave, e)}
      onMover={ajustarArrastre}
      onMoverFin={moverDecorativoFin(d)}
    />
  );

  return (
    <Box ref={contenedorRef} sx={{ position: "relative", flex: 1, overflow: "hidden", bgcolor: COLOR.fondoLienzo }}>
      {tamano.ancho > 0 && (
        <Stage
          ref={stageRef}
          width={tamano.ancho}
          height={tamano.alto}
          scaleX={vista.escala}
          scaleY={vista.escala}
          x={vista.x}
          y={vista.y}
          draggable={moviendoPlano}
          onDragStart={(e) => {
            if (e.target !== stageRef.current && !e.target.hasName("manija")) setOcupado(true);
          }}
          onDragEnd={(e) => {
            setOcupado(false);
            if (e.target === stageRef.current) setVista((v) => ({ ...v, x: e.target.x(), y: e.target.y() }));
          }}
          onWheel={alGirarRueda}
          onMouseDown={(e) => {
            if (moviendoPlano) return;
            const contorno = e.target.name() === "contorno";
            if (e.target !== e.target.getStage() && !(soloElegir && contorno)) return;
            // arrastrar sobre el fondo elige lo que quede adentro: en el diseño,
            // ubicaciones y objetos; en "Asignar tarifas" (también desde el fondo
            // de una ubicación), filas y mesas
            const p = e.target.getStage().getRelativePointerPosition();
            const sector = contorno ? e.target.findAncestor("Group")?.id() : null;
            setArea({ x1: p.x, y1: p.y, x2: p.x, y2: p.y, sumar: e.evt.shiftKey, sector });
          }}
          onMouseMove={(e) => {
            if (!areaRef.current) return;
            const p = e.target.getStage().getRelativePointerPosition();
            setArea((a) => a && { ...a, x2: p.x, y2: p.y });
          }}
          onMouseUp={terminarArea}
          onMouseLeave={terminarArea}
          style={{ cursor: moviendoPlano ? "grab" : "default" }}
        >
          <Layer>
            {vista.escala >= 0.35 && (
              <Grilla
                x={-vista.x / vista.escala}
                y={-vista.y / vista.escala}
                ancho={tamano.ancho / vista.escala}
                alto={tamano.alto / vista.escala}
              />
            )}
            {decorativosAbajo.map(decorativo)}
            {sectoresVisibles.map((s) => (
              <SectorFigura
                key={s.clave}
                sector={s}
                seleccionado={estaElegido(seleccion, "sector", s.clave)}
                enGrupo={seleccion?.tipo === "varios"}
                mesasSeleccionadas={elegidosDe(seleccion, "mesa").filter((e) => e.sector === s.clave).map((e) => e.clave)}
                filasSeleccionadas={elegidosDe(seleccion, "fila").filter((e) => e.sector === s.clave).map((e) => e.indice)}
                onSeleccionarFila={moviendoPlano ? null : (indice, sumar) => elegir({ tipo: "fila", sector: s.clave, indice }, sumar)}
                onMoverFilaFin={(indice, dx, dy) => cambiarSector(cambiar, s.clave, (sector) => ajustarFila(sector, indice, { dx, dy }))}
                filasArrastrables={!soloElegir}
                arrastrable={
                  !moviendoPlano &&
                  !soloElegir &&
                  !(seleccion?.tipo === "fila" && seleccion.sector === s.clave) &&
                  !(seleccion?.tipo === "mesa" && seleccion.sector === s.clave)
                }
                detalle={vista.escala >= ESCALA_DETALLE}
                colorForzado={s.tarifas.length > 0 ? colorDeUbicacion(s, colores) : null}
                onSeleccionar={(e) => {
                  // en "Asignar tarifas" el fondo de la ubicación también arranca el área:
                  // la elige al soltar si no se arrastró
                  if (moviendoPlano || (soloElegir && e?.target?.name() === "contorno")) return;
                  if (soloElegir) seleccionar({ tipo: "sector", clave: s.clave });
                  else elegirObjeto("sector", s.clave, e);
                }}
                onMover={arrastrarSector(s)}
                onMoverFin={moverSectorFin(s)}
                onSeleccionarMesa={(mesa, sumar) => !moviendoPlano && elegir({ tipo: "mesa", sector: s.clave, clave: mesa.clave }, sumar)}
                onMoverMesa={ajustarArrastre}
                onMoverMesaFin={(mesa, e) => {
                  const { x, y } = e.target.position();
                  // la caja se reacomoda y la mesa puede volver a la misma posición
                  // relativa: sin esto Konva la deja donde se soltó
                  e.target.position({ x: mesa.x, y: mesa.y });
                  cambiarSector(cambiar, s.clave, (sector) => moverMesa(sector, mesa.clave, { x: Math.round(x), y: Math.round(y) }));
                }}
              />
            ))}
            {decorativosArriba.map(decorativo)}
            <Transformer
              ref={transformerRef}
              resizeEnabled={Boolean(redimensionable)}
              rotateEnabled={Boolean(seleccionado)}
              shouldOverdrawWholeArea={seleccion?.tipo === "varios" && !soloElegir}
              keepRatio={false}
              rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
              anchorSize={9}
              anchorCornerRadius={2}
              anchorStroke={COLOR.acento}
              anchorFill="#ffffff"
              borderStroke={COLOR.acento}
              rotateAnchorOffset={26}
              ignoreStroke
              onTransformStart={() => setOcupado(true)}
              onTransformEnd={(e) => {
                setOcupado(false);
                transformarFin(e);
              }}
              boundBoxFunc={(viejo, nuevo) => (nuevo.width < 10 || nuevo.height < 10 ? viejo : nuevo)}
            />
            {seleccion?.tipo === "fila" && !soloElegir && !moviendoPlano && !ocupado && (() => {
              const sector = mapa.sectores.find((x) => x.clave === seleccion.sector);
              return sector?.en_mapa ? (
                <ManijasFila
                  sector={sector}
                  indice={seleccion.indice}
                  escala={vista.escala}
                  onAjustar={(cambios) =>
                    cambiarSector(cambiar, sector.clave, (x) => ajustarFila(x, seleccion.indice, cambios), {
                      agrupar: `${sector.clave}-fila-${seleccion.indice}-manija`,
                    })
                  }
                />
              ) : null;
            })()}
            {seleccion?.tipo === "sector" && seleccionado && !soloElegir && !moviendoPlano && !ocupado &&
              seleccionado.en_mapa && tipoDe(seleccionado) === TIPO_UBICACION.MESAS && (
                <ManijasMesas
                  sector={seleccionado}
                  escala={vista.escala}
                  onCambiar={(ancho) =>
                    cambiarSector(cambiar, seleccionado.clave, (s) => acomodarMesas(s, ancho), { agrupar: `${seleccionado.clave}-mesas` })
                  }
                />
              )}
            {seleccion?.tipo === "sector" && seleccionado && !soloElegir && !moviendoPlano && !ocupado &&
              seleccionado.en_mapa && tipoDe(seleccionado) === TIPO_UBICACION.FILAS && (
                <ManijasFilas
                  sector={seleccionado}
                  escala={vista.escala}
                  onCambiar={(cambios) =>
                    cambiarSector(cambiar, seleccionado.clave, (s) => aplicarFilas(s, { ...configFilas(s), ...cambios }), {
                      agrupar: `${seleccionado.clave}-manija`,
                    })
                  }
                />
              )}
            {area && (
              <Rect
                x={Math.min(area.x1, area.x2)}
                y={Math.min(area.y1, area.y2)}
                width={Math.abs(area.x2 - area.x1)}
                height={Math.abs(area.y2 - area.y1)}
                fill={conAlfa(COLOR.acento, 0.08)}
                stroke={COLOR.acento}
                strokeWidth={1 / vista.escala}
                dash={[6 / vista.escala, 4 / vista.escala]}
                listening={false}
              />
            )}
          </Layer>
        </Stage>
      )}

      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: "10px",
          border: `1px solid ${COLOR.borde}`,
          boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Tooltip title="Seleccionar (V)">
          <IconButton
            size="small"
            onClick={() => setHerramienta("seleccionar")}
            sx={herramienta === "seleccionar" ? { bgcolor: COLOR.acento, color: "#fff", "&:hover": { bgcolor: COLOR.acento } } : {}}
          >
            <NearMe fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Mover el plano (H o mantené la barra espaciadora)">
          <IconButton
            size="small"
            onClick={() => setHerramienta("mano")}
            sx={herramienta === "mano" ? { bgcolor: COLOR.acento, color: "#fff", "&:hover": { bgcolor: COLOR.acento } } : {}}
          >
            <PanTool fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Alejar">
          <IconButton size="small" onClick={() => zoomEn(vista.escala / 1.25)}>
            <Remove fontSize="small" />
          </IconButton>
        </Tooltip>
        <Select
          size="small"
          variant="standard"
          disableUnderline
          value=""
          displayEmpty
          renderValue={() => `${Math.round(vista.escala * 100)}%`}
          onChange={(e) => (e.target.value === "ajustar" ? ajustar() : zoomEn(e.target.value))}
          sx={{ minWidth: 64, fontSize: 14, textAlign: "center" }}
        >
          {NIVELES_ZOOM.map((n) => (
            <MenuItem key={n} value={n}>
              {Math.round(n * 100)}%
            </MenuItem>
          ))}
          <MenuItem value="ajustar">Ajustar a la pantalla</MenuItem>
        </Select>
        <Tooltip title="Acercar">
          <IconButton size="small" onClick={() => zoomEn(vista.escala * 1.25)}>
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ajustar a la pantalla">
          <IconButton size="small" onClick={ajustar}>
            <FitScreen fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
      <HerramientaPorTecla setHerramienta={setHerramienta} />
    </Box>
  );
});

// V y H cambian de herramienta, como en los editores de diseño
const HerramientaPorTecla = ({ setHerramienta }) => {
  useEffect(() => {
    const alApretar = (e) => {
      if (esCampoDeTexto(document.activeElement) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "v" || e.key === "V") setHerramienta("seleccionar");
      if (e.key === "h" || e.key === "H") setHerramienta("mano");
    };
    window.addEventListener("keydown", alApretar);
    return () => window.removeEventListener("keydown", alApretar);
  }, [setHerramienta]);
  return null;
};

export default LienzoMapa;
